// Copyright (c) Microsoft Corporation.
// Licensed under the MIT License.

import { Toast, ToastBody, ToastTitle, useToastController } from '@fluentui/react-components';
import { IFeedbackLaunchParams } from '@microsoft/vscodeedu-api/dist-esm/feedback/feedback-options';
import { getFeedbackProvider } from '@microsoft/vscodeedu-api/dist-esm/feedback/feedback-provider';
import React, { useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import useAsyncEffect from 'use-async-effect';
import { useConfig, useTheme } from '../../data';
import { traceEvent, trackError } from '../../utilities';

interface InAppFeedbackContextValue {
    launch: (metadata?: IFeedbackLaunchMetadata) => void;
    enabled: boolean;
}

const InAppFeedbackContext = React.createContext<InAppFeedbackContextValue>({
    launch: (_?: IFeedbackLaunchMetadata) => {},
    enabled: false,
});

interface InAppFeedbackProviderProps {
    children: React.ReactNode;
}

interface IFeedbackLaunchMetadata {
    courseId?: string;
}

const InAppFeedbackProvider: React.FC<InAppFeedbackProviderProps> = ({ children }) => {
    const feedbackDivRef = useRef<HTMLDivElement>(document.createElement('div'));
    const feedbackProvider = getFeedbackProvider(feedbackDivRef.current);
    const { ocvFeedbackAppId, ocvFeedbackHostName, useOcvProdEndpoint } = useConfig();
    const [isInitialized, setIsInitialized] = React.useState(false);
    const [theme] = useTheme();
    const [canDisplay, setCanDisplay] = React.useState(false);
    const { dispatchToast } = useToastController('app-toaster');

    const launch = useCallback(
        async (metadata?: IFeedbackLaunchMetadata) => {
            if (!isInitialized || !canDisplay) {
                dispatchToast(
                    <Toast appearance="inverted">
                        <ToastTitle>
                            <FormattedMessage
                                description="Title of feedback icon group"
                                defaultMessage="Feedback could not be loaded at this time."
                            />
                        </ToastTitle>
                        <ToastBody>
                            <FormattedMessage
                                description="Body of feedback icon group"
                                defaultMessage="Please try again later."
                            />
                        </ToastBody>
                    </Toast>,
                    { intent: 'error' }
                );
                return;
            }
            const launchParams: IFeedbackLaunchParams = {
                theme: theme.profileTheme,
                metadataJson: JSON.stringify({ commit: process.env.REACT_APP_VERSION, ...metadata }),
            };
            try {
                await feedbackProvider.launch(launchParams);
            } catch (e) {
                trackError(e);
                dispatchToast(
                    <Toast appearance="inverted">
                        <ToastTitle>
                            <FormattedMessage
                                description="Title of feedback icon group"
                                defaultMessage="Error opening feedback form."
                            />
                        </ToastTitle>
                        <ToastBody>
                            <FormattedMessage
                                description="Body of feedback icon group"
                                defaultMessage="Please try again later."
                            />
                        </ToastBody>
                    </Toast>,
                    { intent: 'error' }
                );
            }
        },
        [isInitialized, canDisplay, theme.profileTheme, dispatchToast, feedbackProvider]
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useAsyncEffect(async () => {
        try {
            await feedbackProvider.initialize({
                appId: Number(ocvFeedbackAppId),
                hostName: ocvFeedbackHostName,
                isProduction: useOcvProdEndpoint === 'true',
                locale: 'en',
                userAgent: navigator.userAgent,
            });
            if (!feedbackProvider.canDisplay) {
                traceEvent('in_app_feedback_cannot_display');
            }
        } catch (e) {
            trackError(e, 'Failed to initialize feedback provider');
            setIsInitialized(false);
            setCanDisplay(false);
            return;
        }
        setIsInitialized(true);
        setCanDisplay(feedbackProvider.canDisplay);
    }, [feedbackProvider, ocvFeedbackAppId, ocvFeedbackHostName, useOcvProdEndpoint]);

    return (
        <InAppFeedbackContext.Provider value={{ launch, enabled: canDisplay && isInitialized }}>
            {children}
            <div ref={feedbackDivRef} style={{ display: 'none' }} />
        </InAppFeedbackContext.Provider>
    );
};

export { InAppFeedbackContext, InAppFeedbackProvider };
